import { useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { updateCompanyRules } from '../store/redux/actions/userActionCreator';
import { useSignalRConnection } from './useSignalRConnection';

/**
 * Hook that subscribes to SignalR for real-time company rules updates.
 * When an HR/Admin updates company rules, all users in the company will receive
 * the update in real-time without needing to refresh or re-login.
 */
export const useCompanyRulesSignalR = () => {
  const dispatch = useDispatch();
  const { subscribe, subscribeToCompanyGroup, isConnected } = useSignalRConnection();

  const companyId = useSelector((state: any) => state.user?.company?.id);
  const isLoggedIn = useSelector((state: any) => state.user?.isLoggedIn);

  // Handler for company rules updates
  const handleCompanyRulesUpdate = useCallback((updatedRules: any) => {
    console.log('CompanyRulesUpdated received:', updatedRules);
    // Exclude id and companyId to avoid overwriting company.id in Redux
    if (updatedRules) {
      const { id, companyId: _, ...rulesOnly } = updatedRules;
      console.log('Dispatching updateCompanyRules with:', rulesOnly);
      dispatch(updateCompanyRules(rulesOnly));
    }
  }, [dispatch]);

  useEffect(() => {
    if (!isLoggedIn || !companyId) return;

    // Subscribe to the company group
    subscribeToCompanyGroup(companyId).catch(err => {
      console.error('Failed to subscribe to company group:', err);
    });

    // Subscribe to the CompanyRulesUpdated event
    const unsubscribe = subscribe('CompanyRulesUpdated', handleCompanyRulesUpdate);

    return () => {
      unsubscribe();
    };
  }, [isLoggedIn, companyId, subscribe, subscribeToCompanyGroup, handleCompanyRulesUpdate]);

  return { isConnected };
};

export default useCompanyRulesSignalR;
